import PropTypes from 'prop-types';

import useFeaturePermission from '../hooks/useFeaturePermission';
import { FEATURE_PERMISSION_TYPE } from '../helpers';

const propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  purchasedEventFeatures: PropTypes.arrayOf(PropTypes.string),
  searchSubscriptionFeatures: PropTypes.arrayOf(PropTypes.string),
  purchasedEventAddonFeatures: PropTypes.arrayOf(PropTypes.string),
  albumFolderId: PropTypes.string,
  fallbackContent: PropTypes.any,
};

const FeaturePermissionBlock = ({
  children,
  // Initially, features were bundled together, but there are might be situations
  // in which we want to render A or B depending on the type, and it was not convenient
  // to have these bundled, as one of the 2 permissions (whichever it was) could pass the
  // check and render. This problem does not exist with the hook, since in the end, we do check
  // for the specific featureId. (i.e: if (featurePermissionItem.some_event_feature.enabled => do something).)
  purchasedEventFeatures = [],
  searchSubscriptionFeatures = [],
  purchasedEventAddonFeatures = [],
  albumFolderId,
  fallbackContent,
}) => {
  const featurePermissionItem = useFeaturePermission({
    features: [
      ...purchasedEventFeatures,
      ...searchSubscriptionFeatures,
      ...purchasedEventAddonFeatures,
    ],
    albumFolderId,
  });

  const enabledFeature = (() => {
    // Fallback for loading states, PRO content, contributors, etc...
    // All features would return enabled true, so it doesn't really matter.
    // We should be rendering children.
    if (!featurePermissionItem.type) {
      const features = [
        ...purchasedEventFeatures,
        ...searchSubscriptionFeatures,
        ...purchasedEventAddonFeatures,
      ];

      return (
        !features.length || features.find(f => featurePermissionItem[f].enabled)
      );
    }

    if (
      featurePermissionItem.type ===
      FEATURE_PERMISSION_TYPE.PURCHASED_EVENT_ADDON_FEATURES
    ) {
      return (
        !purchasedEventAddonFeatures?.length ||
        purchasedEventAddonFeatures.find(f => featurePermissionItem[f].enabled)
      );
    }

    if (
      featurePermissionItem.type ===
      FEATURE_PERMISSION_TYPE.PURCHASED_EVENT_FEATURES
    ) {
      return (
        !purchasedEventFeatures?.length ||
        purchasedEventFeatures.find(f => featurePermissionItem[f].enabled)
      );
    }

    return (
      !searchSubscriptionFeatures?.length ||
      searchSubscriptionFeatures.find(f => featurePermissionItem[f].enabled)
    );
  })();

  // Any enabled feature [from the type's array] will be enough to render. It is up to parent
  // components to check for which of the features was specifically enabled.
  if (enabledFeature) {
    return typeof children === 'function'
      ? children(featurePermissionItem)
      : children || null;
  }

  return typeof fallbackContent === 'function'
    ? fallbackContent(featurePermissionItem)
    : fallbackContent || null;
};

FeaturePermissionBlock.propTypes = propTypes;

export default FeaturePermissionBlock;
